import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRutWithoutDots, toCapitalCase } from '@/utils';
import { useNavigate } from 'react-router-dom';
import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import { ROUTE_DEFINITIONS } from '@/routes';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import { employeeService, type Employee, type ContractStatusFilter } from '@/api/domains/employee';
import { useHoldingCompanies } from '@/api/domains/catalog';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';

// Filters state
interface EmployeeFiltersState {
  companyIds: string[];
  statusFilter: 'V' | 'NV';
  contractStatusFilter: ContractStatusFilter;
}

interface UseEmployeeListResult {
  rows: Employee[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (p: number) => void;
  setPageSize: (s: number) => void;
  filters: EmployeeFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<EmployeeFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<Employee>[];
  actions: TableAction<Employee>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  togglingIds: Set<number>;
  toggleEmployeeStatus: (row: Employee) => Promise<void>;
}
export function useEmployeeList(): UseEmployeeListResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();
  const { companies } = useHoldingCompanies();
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());
  const navigate = useNavigate();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<EmployeeFiltersState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<Employee> | null> => {
      const effectiveCompanyIds = params.filters.companyIds.length === 0
        ? companies.map((company) => String(company.id))
        : params.filters.companyIds;
      const response = await employeeService.list({
        page: params.page,
        pageSize: params.pageSize,
        companyIds: effectiveCompanyIds,
        statusFilter: params.filters.statusFilter,
        contractStatusFilter: params.filters.contractStatusFilter,
        searchText: params.searchText,
        sort: params.sort
          ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
          : undefined,
      }, signal);
      if (!response.success || !response.data) {
        showApiError(response);
        return null;
      }
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [companies, showApiError],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    refetch,
  } = usePaginatedList<Employee, EmployeeFiltersState>({
    fetchFn,
    initialFilters: { companyIds: [], statusFilter: 'V', contractStatusFilter: 'ALL' },
    initialSort: { key: 'fullName', direction: 'ASC' },
    orderByMap: {
      rut: 'rut',
      fullName: 'fullName',
      company: 'companyName',
      contract: 'contractNumber',
      status: 'status',
    },
    deduplicate: true,
    enabled: companies.length > 0,
  });

  const companyOptions: SimpleFilter['options'] = useMemo(() => (
    companies.map((company) => ({ value: String(company.id), label: company.name }))
  ), [companies]);

  const contractStatusOptions: SimpleFilter['options'] = useMemo(() => ([
    { value: 'ACTIVE', label: translate('employee:filters.contractStatus.options.active') },
    { value: 'INACTIVE', label: translate('employee:filters.contractStatus.options.inactive') },
    { value: 'ALL', label: translate('employee:filters.contractStatus.options.all') },
  ]), [translate]);

  const filterConfig: SimpleFilter[] = useMemo(() => ([
    { key: 'companyIds', label: 'Empresas', multiple: true, options: companyOptions, placeholder: 'Seleccionar empresas' },
    {
      key: 'contractStatusFilter',
      label: translate('employee:filters.contractStatus.label'),
      multiple: false,
      options: contractStatusOptions,
      defaultValue: 'ALL',
      placeholder: translate('employee:filters.contractStatus.label'),
    },
  ]), [companyOptions, contractStatusOptions, translate]);

  const toggleEmployeeStatus = useCallback(async (row: Employee) => {
    const enabling = row.status === 'inactive';
    setTogglingIds((previous) => new Set(previous).add(row.id));
    const response = await employeeService.enableDisable({
      employeeId: row.id,
      enable: enabling,
      companyId: Number(filters.companyIds[0]),
    });
    if (!response.success) {
      showNotification('error', 'No se pudo actualizar estado', 5000);
    } else {
      showNotification('success', `Colaborador ${enabling ? 'habilitado' : 'deshabilitado'}`, 4000);
      refetch();
    }
    setTogglingIds((previous) => {
      const next = new Set(previous);
      next.delete(row.id);
      return next;
    });
  }, [filters.companyIds, showNotification, refetch]);

  const columns: TableColumn<Employee>[] = useMemo(() => [
    { key: 'rut', header: 'RUT', sortable: true, render: (row: Employee) => formatRutWithoutDots(row.rut) },
    { key: 'fullName', header: 'Nombre', sortable: true, maxWidth: 350, render: (row: Employee) => (
      <Text
        variant="subheader-medium"
        color={colors.important.main}
        as="span"
        onClick={() => { void navigate(ROUTE_DEFINITIONS.EMPLEADOS_PERFIL.path.replace(':employeeId', String(row.id))); }}
        className="cursor-pointer transition-colors hover:opacity-80"
      >
        {toCapitalCase(row.fullName)}
      </Text>
    ) },
    { key: 'company', header: 'Empresa', sortable: true, render: (row: Employee) => row.companyName },
    { key: 'contract', header: 'Contrato', sortable: true, render: (row: Employee) => row.contractNumber },
    { key: 'status', header: 'Estado', sortable: true, render: (row: Employee) => (
      <Text variant="subheader-regular" as="span">
        {row.status === 'active'
          ? translate('employee:listView.status.active')
          : translate('employee:listView.status.inactive')}
      </Text>
    ) },
  ], [navigate, translate]);

  const actions: TableAction<Employee>[] = useMemo(() => [
    {
      id: 'enable',
      label: 'Habilitar',
      icon: 'userPlus',
      onClick: (row) => { void toggleEmployeeStatus(row); },
      color: colors.feedback.success400,
      disabled: (row) => togglingIds.has(row.id),
      isLoading: (row) => togglingIds.has(row.id),
      hidden: (row) => row.status === 'active',
    },
    {
      id: 'disable',
      label: 'Deshabilitar',
      icon: 'userMinus',
      onClick: (row) => { void toggleEmployeeStatus(row); },
      color: colors.feedback.error400,
      disabled: (row) => togglingIds.has(row.id),
      isLoading: (row) => togglingIds.has(row.id),
      hidden: (row) => row.status === 'inactive',
    },
  ], [toggleEmployeeStatus, togglingIds]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    togglingIds,
    toggleEmployeeStatus,
  };
}

export default useEmployeeList;